import { parseModel, typeLabel } from "../../../../../resources/web-components/Inputs/entityText.mjs";
import { isValidInternalRoute } from "../../../help/widgets/types.js";

function entityRoute(entityType, entityId) {
  return `/Database/${entityType}/${encodeURIComponent(entityId)}`;
}

function entityPill(segment) {
  return {
    label: segment.number,
    meta: [typeLabel(segment.entityType), segment.title].filter(Boolean).join(" · "),
    route: entityRoute(segment.entityType, segment.entityId),
  };
}

function textPieces(text) {
  return parseModel(text).map((segment) => (
    segment.type === "text" ? { type: "text", text: segment.text } : { type: "pill", pill: entityPill(segment) }
  ));
}

function escapeRegExp(value) {
  return String(value).replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
}

function replaceLabel(text, label, pill) {
  const pattern = new RegExp(`(?<![A-Za-z0-9._-])${escapeRegExp(label)}(?![A-Za-z0-9_-])(?!\\.[A-Za-z0-9_-])`, "g");
  const pieces = [];
  let cursor = 0;
  let found = false;
  for (const match of text.matchAll(pattern)) {
    found = true;
    if (match.index > cursor) pieces.push({ type: "text", text: text.slice(cursor, match.index) });
    pieces.push(pill);
    cursor = match.index + label.length;
  }
  if (!found) return null;
  if (cursor < text.length) pieces.push({ type: "text", text: text.slice(cursor) });
  return pieces;
}

/**
 * A stored answer lists record numbers, then repeats those records as links.
 * Move each of those links onto the number already written in the list.
 */
function placeRecordPills(pieces) {
  let end = pieces.length;
  const trailing = [];
  while (end > 0) {
    const piece = pieces[end - 1];
    if (piece.type === "pill") {
      trailing.unshift(piece);
      end -= 1;
      continue;
    }
    if (piece.type === "text" && /^[\s,]*$/.test(piece.text) && trailing.some((item) => item.type === "pill")) {
      trailing.unshift(piece);
      end -= 1;
      continue;
    }
    break;
  }
  const pills = trailing.filter((piece) => piece.type === "pill");
  if (!pills.length || !trailing.some((piece) => piece.type === "text")) return pieces;

  const body = pieces.slice(0, end);
  const unused = [];
  for (const pill of [...pills].sort((left, right) => right.pill.label.length - left.pill.label.length)) {
    let placed = false;
    for (let index = 0; index < body.length; index += 1) {
      const piece = body[index];
      if (piece.type !== "text") continue;
      const replacement = replaceLabel(piece.text, pill.pill.label, pill);
      if (!replacement) continue;
      body.splice(index, 1, ...replacement);
      placed = true;
      break;
    }
    if (!placed) unused.push(pill);
  }
  const hasBody = body.some((piece) => piece.type !== "text" || piece.text.trim());
  unused.forEach((pill, index) => {
    body.push({ type: "text", text: index === 0 && hasBody ? "\n\n" : ", " });
    body.push(pill);
  });
  return body;
}

/**
 * Split a stored chat message into markdown text and pills.
 * Bot `Parts` links and `[[type|number|id|title]]` markers become pills.
 */
function messagePieces(message) {
  if (Array.isArray(message?.Parts) && message.Parts.length) {
    const files = [];
    const rest = [];
    for (const part of message.Parts) {
      if (part?.type === "file" && typeof part.label === "string" && typeof part.fileId === "string") {
        files.push({ type: "file", file: { label: part.label, fileId: part.fileId } });
        continue;
      }
      if (part?.type === "link") {
        rest.push(isValidInternalRoute(part.route) && typeof part.label === "string"
          ? { type: "pill", pill: { label: part.label, meta: "", route: part.route } }
          : { type: "text", text: part.label || "" });
        continue;
      }
      rest.push(...textPieces(part?.text || ""));
    }
    return [...placeRecordPills(rest), ...files];
  }
  return textPieces(message?.Message || "");
}

/** Markdown with an inline placeholder element where each pill goes. */
function markdownWithPills(pieces) {
  const pills = [];
  const files = [];
  let source = "";
  for (const piece of pieces) {
    if (piece.type === "pill") {
      source += `<span data-help-pill="${pills.length}"></span>`;
      pills.push(piece.pill);
    } else if (piece.type === "file") {
      source += `<span data-help-file="${files.length}"></span>`;
      files.push(piece.file);
    } else {
      source += piece.text;
    }
  }
  return { source, pills, files };
}

function renderMarkdown(source, { marked, DOMPurify }) {
  return DOMPurify.sanitize(marked.parse(source, { gfm: true, breaks: true }), {
    ADD_ATTR: ["target"],
  });
}

/** The message as plain text, with tags written as their numbers. */
function plainText(value) {
  return parseModel(value).map((segment) => (segment.type === "text" ? segment.text : segment.number)).join("");
}

const MAX_OPTIONS = 3;

/**
 * Choices a bot message offers: `[{ id, label, call: { tool, args } }]`.
 * The conversation is user-writable, so malformed entries are dropped.
 */
function messageOptions(message) {
  if (message?.Role !== "bot" || message.Pending || !Array.isArray(message.Options)) return [];
  return message.Options
    .filter((option) => option
      && typeof option.label === "string" && option.label.trim()
      && option.call && typeof option.call.tool === "string"
      && (option.call.args === undefined || (typeof option.call.args === "object" && option.call.args !== null && !Array.isArray(option.call.args))))
    .slice(0, MAX_OPTIONS)
    .map((option, index) => ({
      id: typeof option.id === "string" ? option.id : `o${index + 1}`,
      label: option.label.trim(),
      call: { tool: option.call.tool, args: option.call.args || {} },
    }));
}

export { entityRoute, markdownWithPills, messageOptions, messagePieces, plainText, renderMarkdown };
